import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { datadogLogs } from "@datadog/browser-logs";
import { AuthBaseUrl, AzureStartUrl, OktaStartUrl } from "./Constants";
import { getUrlWithQuery } from "./Util";

/**
 * Which login providers the backend currently offers. Driven by the backend auth feature
 * toggles so providers can be turned on/off without a frontend deploy.
 */
export interface AuthProviders {
  okta: boolean;
  /**
   * Direct Microsoft Entra ID (Azure AD) login path for `@gerdau.com` users (ADR 0002). When
   * true the login page routes Gerdau addresses straight to Entra instead of Okta.
   */
  azure: boolean;
  egerdau: boolean;
  /**
   * Preferred provider for the login "home page": blank/"okta" = Okta-local,
   * "azure" = Microsoft Entra ID. Drives auto-login and button label.
   */
  defaultIdp?: string;
}

/**
 * The authenticated principal returned by the backend BFF (`GET /auth/me`). Tokens never
 * reach the browser — the session is carried by the HttpOnly QQT_SESSION cookie.
 */
export interface AuthMe {
  subject: string;
  email: string;
  displayName: string;
  provider: string;
  groups: string[];
}

/**
 * Result of `POST /auth/logout`. The backend always clears the server-side session and cookie;
 * `logoutUrl`, when present, is Okta's RP-initiated logout URL the browser must navigate to so
 * the upstream Okta (SSO) session is terminated as well.
 */
export interface AuthLogout {
  logoutUrl?: string | null;
}

/**
 * Parameters accepted by the backend `/auth/okta/start` endpoint. They let an originating
 * application deep-link into a specific IdP / return URL and request a silent SSO attempt.
 */
export interface OktaStartParams {
  idp?: string;
  source?: string;
  return?: string;
  login_hint?: string;
  silent?: boolean;
}

const toQueryValues = (params: OktaStartParams): Record<string, string> => {
  const query: Record<string, string> = {};
  if (params.idp) query.idp = params.idp;
  if (params.source) query.source = params.source;
  if (params.return) query.return = params.return;
  if (params.login_hint) query.login_hint = params.login_hint;
  if (params.silent) query.silent = "true";
  return query;
};

/**
 * Builds the backend `/auth/okta/start` URL. The browser navigates here to begin the Okta
 * Authorization Code flow; the backend performs the full code exchange server-side.
 */
export const getOktaStartUrl = (params: OktaStartParams = {}): string => {
  const query = toQueryValues(params);
  return Object.keys(query).length ? getUrlWithQuery(OktaStartUrl, query) : OktaStartUrl;
};

const redirect = (url: string) => (window.location.href = url);

/** Full-page redirect to begin the Okta login flow. */
export const redirectToOkta = (params: OktaStartParams = {}) => redirect(getOktaStartUrl(params));

/**
 * Parameters accepted by the backend `/auth/azure/start` endpoint. Unlike Okta there is no
 * `idp` field — this path talks to Microsoft Entra ID directly.
 */
export interface AzureStartParams {
  source?: string;
  return?: string;
  login_hint?: string;
  silent?: boolean;
}

const toAzureQueryValues = (params: AzureStartParams): Record<string, string> => {
  const query: Record<string, string> = {};
  if (params.source) query.source = params.source;
  if (params.return) query.return = params.return;
  if (params.login_hint) query.login_hint = params.login_hint;
  if (params.silent) query.silent = "true";
  return query;
};

/**
 * Builds the backend `/auth/azure/start` URL. The browser navigates here to begin the Entra ID
 * Authorization Code flow; the backend performs the full code exchange server-side.
 */
export const getAzureStartUrl = (params: AzureStartParams = {}): string => {
  const query = toAzureQueryValues(params);
  return Object.keys(query).length ? getUrlWithQuery(AzureStartUrl, query) : AzureStartUrl;
};

/** Full-page redirect to begin the Microsoft Entra ID login flow. */
export const redirectToAzure = (params: AzureStartParams = {}) =>
  redirect(getAzureStartUrl(params));

export const AuthApi = createApi({
  reducerPath: "authApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${AuthBaseUrl}/`,
    // Send and receive the HttpOnly session cookie on every BFF call.
    credentials: "include",
    fetchFn: async (input, init) => {
      try {
        return await fetch(input, init);
      } catch (error) {
        datadogLogs.logger.error("[Auth BFF call error]: Unexpected exception during API call:", {
          error: error,
        });
        throw error;
      }
    },
  }),
  endpoints: (build) => ({
    getAuthProviders: build.query<AuthProviders, void>({
      query: () => ({ url: "providers" }),
    }),
    getAuthMe: build.query<AuthMe, void>({
      query: () => ({ url: "me" }),
    }),
    logout: build.mutation<AuthLogout, void>({
      query: () => ({ url: "logout", method: "POST" }),
    }),
  }),
});

export const { useGetAuthProvidersQuery, useGetAuthMeQuery, useLogoutMutation } = AuthApi;
